import {createContext, useContext, useEffect, useMemo, useRef} from 'react'
import posthog from 'posthog-js'

import {
  type AnalyticsProperties,
  identifyAnalyticsUser,
  registerAnalyticsGlobalProperties,
  resetAnalyticsIdentity,
  setAnalyticsAdapter,
  syncAnalyticsIdentity,
  trackAnalyticsEvent,
} from '#/lib/analytics'
import {type SessionAccount} from '#/state/session'
import * as env from '#/env'
import {device} from '#/storage'

const POSTHOG_API_KEY = env.POSTHOG_API_KEY
const POSTHOG_HOST = env.POSTHOG_HOST

type PostHogContextValue = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void
}

type PostHogWebConditions = {
  hasApiKey: boolean
  apiHost: string
  doNotTrack: string | null
  cookiesEnabled: boolean
  hasLocalStorage: boolean
  pageProtocol: string
}

const PostHogContext = createContext<PostHogContextValue | null>(null)
PostHogContext.displayName = 'PostHogContext'

export function usePostHog() {
  return useContext(PostHogContext)
}

let isInitialized = false

function getPostHogWebConditions(): PostHogWebConditions {
  const hasLocalStorage = (() => {
    try {
      return typeof window !== 'undefined' && !!window.localStorage
    } catch {
      return false
    }
  })()
  return {
    hasApiKey: Boolean(POSTHOG_API_KEY),
    apiHost: POSTHOG_HOST,
    doNotTrack:
      typeof navigator !== 'undefined' ? navigator.doNotTrack || null : null,
    cookiesEnabled:
      typeof navigator !== 'undefined' ? navigator.cookieEnabled : false,
    hasLocalStorage,
    pageProtocol:
      typeof window !== 'undefined' ? window.location.protocol : 'unknown',
  }
}

function safeGetIdentityMarker(): string | undefined {
  try {
    return device.get(['analyticsLastIdentifiedDid'])
  } catch {
    return undefined
  }
}

function safeSetIdentityMarker(did: string) {
  try {
    device.set(['analyticsLastIdentifiedDid'], did)
  } catch {
    // Avoid blocking analytics when storage fails in restrictive browsers.
  }
}

function safeClearIdentityMarker() {
  try {
    device.remove(['analyticsLastIdentifiedDid'])
  } catch {
    // Avoid blocking analytics when storage fails in restrictive browsers.
  }
}

function initPostHog() {
  if (isInitialized || !POSTHOG_API_KEY) {
    console.log('[PostHog Web] Skip init - already initialized or no API key')
    return
  }

  console.log('[PostHog Web] Initializing with host:', POSTHOG_HOST)
  const conditions = getPostHogWebConditions()
  console.log('[PostHog Web] Conditions:', conditions)
  if (typeof window !== 'undefined') {
    ;(
      window as Window & {__POSTHOG_CONDITIONS__?: PostHogWebConditions}
    ).__POSTHOG_CONDITIONS__ = conditions
  }

  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST,
    // Enable debug mode in dev
    debug: env.IS_DEV,
    // Session replay settings
    disable_session_recording: false,
    session_recording: {
      // Mask all text inputs by default for privacy
      maskAllInputs: true,
      maskTextSelector: '*',
    },
    // Autocapture settings
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    // Privacy settings
    respect_dnt: true,
    // Persistence
    persistence: 'localStorage+cookie',
    // Bootstrap with anonymous ID
    bootstrap: {
      distinctID: undefined,
    },
    // Loaded callback
    loaded: posthogInstance => {
      console.log('[PostHog Web] Loaded:', {
        distinctId: posthogInstance.get_distinct_id(),
      })
    },
  })

  isInitialized = true
}

export function Provider({
  children,
  currentAccount,
}: {
  children: React.ReactNode
  currentAccount: SessionAccount | undefined
}) {
  const prevDidRef = useRef<string | undefined>(undefined)
  const hasSyncedInitialIdentityRef = useRef(false)
  const hasRegisteredRef = useRef(false)
  const globalProperties: AnalyticsProperties = useMemo(
    () => ({
      environment: env.ENV,
      isDev: env.IS_DEV,
      platform: 'web',
      appVersion: env.RELEASE_VERSION,
    }),
    [],
  )
  const identifyTraits: AnalyticsProperties = useMemo(
    () => ({
      platform: 'web',
      appVersion: env.RELEASE_VERSION,
      bundleIdentifier: env.BUNDLE_IDENTIFIER,
      bundleDate: env.BUNDLE_DATE,
      environment: env.ENV,
    }),
    [],
  )

  // Initialize PostHog on mount
  useEffect(() => {
    if (POSTHOG_API_KEY) {
      initPostHog()
    }
  }, [])

  useEffect(() => {
    if (!POSTHOG_API_KEY) return

    setAnalyticsAdapter({
      track: (eventName, properties) => {
        posthog.capture(eventName, properties)
      },
      identify: (userId, traits) => {
        posthog.identify(userId, traits)
      },
      reset: () => {
        posthog.reset()
      },
      registerGlobalProps: properties => {
        posthog.register(properties)
      },
    })

    if (!hasRegisteredRef.current) {
      registerAnalyticsGlobalProperties(globalProperties)
      hasRegisteredRef.current = true
    }

    return () => {
      setAnalyticsAdapter(null)
    }
  }, [globalProperties])

  // Identify user when account changes
  useEffect(() => {
    if (!POSTHOG_API_KEY) return

    const did = currentAccount?.did
    prevDidRef.current = syncAnalyticsIdentity({
      prevDid: prevDidRef.current,
      currentDid: did,
      hasPersistedIdentifiedDid: Boolean(safeGetIdentityMarker()),
      identifyTraits,
      anonymousGlobalProperties: globalProperties,
      identify: identifyAnalyticsUser,
      reset: resetAnalyticsIdentity,
      registerGlobalProperties: registerAnalyticsGlobalProperties,
      onIdentifySync: identifiedDid => {
        safeSetIdentityMarker(identifiedDid)
      },
      onResetSync: () => {
        safeClearIdentityMarker()
      },
      isInitialSync: !hasSyncedInitialIdentityRef.current,
    })
    hasSyncedInitialIdentityRef.current = true
  }, [currentAccount?.did, globalProperties, identifyTraits])

  const contextValue = useMemo<PostHogContextValue>(
    () => ({
      capture: (eventName: string, properties?: Record<string, unknown>) => {
        trackAnalyticsEvent(
          eventName,
          (properties as AnalyticsProperties | undefined) ?? {},
        )
      },
    }),
    [],
  )

  // If no API key, render children without context
  if (!POSTHOG_API_KEY) {
    console.log('[PostHog Web] Disabled - no API key found')
    return <>{children}</>
  }

  return (
    <PostHogContext.Provider value={contextValue}>
      {children}
    </PostHogContext.Provider>
  )
}
